import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useValidityReport } from './hooks';
import type { ValidityBadge, ValidityReportItem } from './types';

const BADGE_STYLES: Record<ValidityBadge, string> = {
  Valid: 'bg-green-100 text-green-700',
  Expiring: 'bg-amber-100 text-amber-800',
  Expired: 'bg-red-100 text-red-700',
  NotYetEffective: 'bg-slate-100 text-slate-600'
};

const BADGE_LABEL: Record<ValidityBadge, string> = {
  Valid: 'Valid',
  Expiring: 'Expiring',
  Expired: 'Expired',
  NotYetEffective: 'Not yet effective'
};

function humanizeKind(kind: string): string {
  return {
    PowerOfAttorney: 'Power of attorney',
    VetAuthorization: 'Vet authorization',
    IncapacitationDirective: 'Incapacitation directive',
    Other: 'Other'
  }[kind] ?? kind;
}

export function ValidityReportPage() {
  usePageTitle('Document validity');
  const { data, isLoading, error } = useValidityReport();

  // Backend already sorts problems first; group by client preserving that order.
  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; items: ValidityReportItem[] }>();
    for (const i of data ?? []) {
      const entry = map.get(i.customerId) ?? { name: i.customerName, items: [] };
      entry.items.push(i);
      map.set(i.customerId, entry);
    }
    return [...map.values()];
  }, [data]);

  const lapsing = (data ?? []).filter((i) => i.lapsesBeforeNextVisit);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Document validity</h1>
          <p className="text-slate-500 text-sm mt-1">When each signed agreement takes effect and lapses.</p>
        </div>
        <Link to="/documents/sent" className="text-sm text-sky-600 hover:text-sky-700 whitespace-nowrap">
          ← Signing status
        </Link>
      </div>

      {lapsing.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle className="text-amber-600 shrink-0" size={20} />
          <div className="text-sm text-amber-900">
            <strong>{lapsing.length}</strong> agreement{lapsing.length === 1 ? '' : 's'} lapse before the client's next
            visit. Renew before you're exposed.
          </div>
        </div>
      )}

      {isLoading && <p className="text-slate-500">Loading…</p>}
      {error && <p className="text-red-600">Could not load the validity report.</p>}
      {data && data.length === 0 && (
        <p className="text-slate-500 bg-white border border-slate-200 rounded-xl p-6">
          No signed documents yet.
        </p>
      )}

      {grouped.map((group) => (
        <section key={group.name} className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{group.name}</h2>
          <div className="space-y-2">
            {group.items.map((item) => (
              <Row key={item.documentInstanceId} item={item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Row({ item }: { item: ValidityReportItem }) {
  return (
    <div className={`bg-white border rounded-xl p-4 ${item.lapsesBeforeNextVisit ? 'border-amber-300' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-slate-800 truncate">{item.templateName}</div>
          <div className="text-xs text-slate-500">{humanizeKind(item.kind)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${BADGE_STYLES[item.badge]}`}>
            {BADGE_LABEL[item.badge]}
          </span>
          <a
            href={item.verificationUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 text-sm"
            title="QR verification page"
          >
            <ShieldCheck size={15} />
          </a>
        </div>
      </div>

      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-xs mt-2">
        <dt className="text-slate-500">Effective</dt>
        <dd className="text-slate-700">{item.effectiveUtc ? new Date(item.effectiveUtc).toLocaleDateString() : 'On signing'}</dd>
        <dt className="text-slate-500">Expires</dt>
        <dd className="text-slate-700">{new Date(item.expiresUtc).toLocaleDateString()}</dd>
        {item.nextVisitStartUtc && (
          <>
            <dt className="text-slate-500">Next visit</dt>
            <dd className={item.lapsesBeforeNextVisit ? 'text-amber-700 font-medium' : 'text-slate-700'}>
              {new Date(item.nextVisitStartUtc).toLocaleDateString()}
              {item.lapsesBeforeNextVisit ? ' — lapses before this visit' : ''}
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}
